import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";
const BASE_URL = process.env.REACT_APP_BACKEND_URL;
const BookmarkCtx = createContext(null);
const guestIds = () => {
  try { return new Set(Object.keys(localStorage).filter(k => k.startsWith("dnh:guest:bk:") && localStorage.getItem(k) === "1").map(k => k.slice(13))); }
  catch { return new Set(); }
};
export const BookmarkProvider = ({ children }) => {
  const { user, loading } = useAuth();
  const [ids, setIds] = useState(new Set());
  const [syncing, setSyncing] = useState(true);
  const [feedback, setFeedback] = useState({});
  const idsRef = useRef(ids);
  const pending = useRef(new Set());
  const owner = useRef(null);
  const update = useCallback(next => { idsRef.current = next; setIds(next); }, []);
  useEffect(() => {
    if (loading) return;
    let active = true;
    owner.current = user?.user_id || null;
    setFeedback({});
    update(new Set());
    // Remove legacy, unscoped account bookmarks rather than transfer them to another reader.
    try { Object.keys(localStorage).filter(k => k.startsWith("dnh:bk:")).forEach(k => localStorage.removeItem(k)); } catch {}
    if (!user) { update(guestIds()); setSyncing(false); return; }
    setSyncing(true);
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/user/saved`, { credentials: "include" });
        if (!res.ok) throw new Error();
        const { saved } = await res.json();
        if (active) update(new Set(saved.map(s => s.id)));
        const votes = await fetch(`${BASE_URL}/api/user/feedback`, { credentials: "include" });
        if (votes.ok) { const d = await votes.json(); if (active) setFeedback(Object.fromEntries(d.feedback.map(f => [f.story_id, f]))); }
      } catch { if (active) toast.error("Saved stories could not be loaded. Please try again."); }
      finally { if (active) setSyncing(false); }
    })();
    return () => { active = false; };
  }, [user, loading, update]);
  const toggleBookmark = useCallback(async id => {
    if (!id || syncing || pending.current.has(id)) return idsRef.current.has(id);
    const was = idsRef.current.has(id), currentOwner = owner.current;
    pending.current.add(id);
    try {
      if (currentOwner) {
        const res = await fetch(`${BASE_URL}/api/user/saved${was ? `/${encodeURIComponent(id)}` : ""}`, {
          method: was ? "DELETE" : "POST", credentials: "include", headers: { "Content-Type": "application/json" }, body: was ? undefined : JSON.stringify({ story_id: id }) });
        if (!res.ok && !(was && res.status === 404)) throw new Error();
      } else {
        if (was) localStorage.removeItem(`dnh:guest:bk:${id}`); else localStorage.setItem(`dnh:guest:bk:${id}`, "1");
      }
      if (currentOwner !== owner.current) return false;
      const next = new Set(idsRef.current); if (was) next.delete(id); else next.add(id); update(next);
      return !was;
    } catch { toast.error("Bookmark could not be saved. Please try again."); throw new Error("Bookmark failed"); }
    finally { pending.current.delete(id); }
  }, [syncing, update]);
  const saveFeedback = async (id, value) => {
    const currentOwner = owner.current;
    if (!currentOwner) return false;
    const r = await fetch(`${BASE_URL}/api/user/feedback/${encodeURIComponent(id)}`, { method: "PUT", credentials: "include", headers: {"Content-Type": "application/json"}, body: JSON.stringify(value) });
    if (!r.ok) throw new Error("Feedback could not be saved");
    if (currentOwner === owner.current) setFeedback(prev => ({...prev, [id]: value}));
    return true;
  };
  return <BookmarkCtx.Provider value={{ bookmarkedIds: ids, isBookmarked: id => ids.has(id), toggleBookmark, syncing, feedback, saveFeedback, signedIn: !!user }}>{children}</BookmarkCtx.Provider>;
};
export const useBookmarks = () => useContext(BookmarkCtx);